import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useStudio } from "@/context/StudioContext";
import { LoadingState, ErrorState, EmptyState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Factory, Sparkles, Loader2, Ban, CheckCircle2, TrendingUp, Users, Tag,
  Calendar, Play, Pause, DollarSign, Rocket, Palette, RefreshCw, Image,
  AlertTriangle,
} from "lucide-react";

const inputCls = "bg-slate-900 border-slate-800 text-slate-100 focus-visible:ring-violet-500/30";

function ScoreBar({ label, v }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-24 text-slate-500">{label}</span>
      <div className="flex-1 h-1.5 rounded bg-slate-800 overflow-hidden">
        <div className="h-full bg-violet-500" style={{ width: `${v || 0}%` }} />
      </div>
      <span className="w-8 text-right text-slate-300">{v ?? "—"}</span>
    </div>
  );
}

/* ---- Palette swatch display ---- */
function PaletteSwatches({ palette }) {
  if (!palette) return null;
  const entries = [
    { key: "primary", label: "Əsas" },
    { key: "secondary", label: "İkincil" },
    { key: "accent", label: "Vurğu" },
    { key: "background", label: "Fon" },
  ];
  return (
    <div className="flex flex-wrap gap-2" data-testid="palette-swatches">
      {entries.map(({ key, label }) => {
        const hex = palette[key];
        if (!hex) return null;
        return (
          <div key={key} className="flex items-center gap-1.5" data-testid={`swatch-${key}`}>
            <div
              className="w-5 h-5 rounded border border-slate-700 shrink-0"
              style={{ backgroundColor: hex }}
              title={`${label}: ${hex}`}
            />
            <span className="text-[10px] text-slate-400">{hex}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ---- Brand Kit section inside ConceptCard ---- */
function BrandKitSection({ concept, onUpdated }) {
  const [generating, setGenerating] = useState(false);
  const [kitError, setKitError] = useState(null);
  const kit = concept.brand_kit;

  const generate = async () => {
    setGenerating(true);
    setKitError(null);
    try {
      await api.post(`/factory/concepts/${concept.id}/brand-kit`);
      toast.success("Brend dəsti yaradıldı · Brand kit generated");
      onUpdated();
    } catch (e) {
      const msg = apiError(e);
      setKitError(msg);
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  if (!kit) {
    return (
      <div className="mt-3 pt-3 border-t border-slate-800/60" data-testid={`brandkit-empty-${concept.id}`}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-slate-500 flex items-center gap-1">
            <Palette className="w-3 h-3" /> Brend dəsti yoxdur
          </span>
        </div>
        <Button
          size="sm" variant="outline" onClick={generate} disabled={generating}
          className="border-slate-700 text-slate-300 hover:bg-slate-800 w-full text-xs"
          data-testid={`generate-brandkit-${concept.id}`}
        >
          {generating
            ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
            : <Image className="w-3.5 h-3.5 mr-1.5" />}
          {generating ? "Yaradılır…" : "Brend dəsti yarat"}
        </Button>
        {kitError && (
          <p className="text-[10px] text-rose-400 mt-1.5 flex items-start gap-1" data-testid={`brandkit-error-${concept.id}`}>
            <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> {kitError}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="mt-3 pt-3 border-t border-slate-800/60" data-testid={`brandkit-${concept.id}`}>
      <div className="flex items-start gap-3 mb-2">
        {kit.logo?.url && (
          <img
            src={kit.logo.url}
            alt={`${kit.brand_name} logo`}
            className="w-12 h-12 rounded-lg border border-slate-700 object-cover shrink-0 bg-slate-900"
            data-testid={`brandkit-logo-${concept.id}`}
          />
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-100 truncate" data-testid={`brandkit-name-${concept.id}`}>
            {kit.brand_name}
          </p>
          {kit.tagline && (
            <p className="text-[11px] text-slate-400 italic truncate" data-testid={`brandkit-tagline-${concept.id}`}>
              {kit.tagline}
            </p>
          )}
        </div>
      </div>

      <PaletteSwatches palette={kit.palette} />

      <div className="flex items-center justify-between mt-2">
        <span className="text-[10px] text-slate-600">
          {kit.provider_label} · ${kit.cost_usd?.toFixed(3)}
        </span>
        <Button
          size="sm" variant="ghost" onClick={generate} disabled={generating}
          className="text-[10px] text-slate-500 hover:text-slate-300 h-6 px-2"
          data-testid={`regenerate-brandkit-${concept.id}`}
        >
          {generating
            ? <Loader2 className="w-3 h-3 animate-spin" />
            : <><RefreshCw className="w-3 h-3 mr-1" /> Yenidən yarat</>}
        </Button>
      </div>
      {kitError && (
        <p className="text-[10px] text-rose-400 mt-1 flex items-start gap-1" data-testid={`brandkit-error-${concept.id}`}>
          <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> {kitError}
        </p>
      )}
    </div>
  );
}

function ConceptCard({ c, onCreated }) {
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/factory/concepts/${c.id}/create-channel`);
      toast.success(`Channel created (${data.ideas_seeded} ideas seeded)`);
      onCreated();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-[#111827] border border-slate-800/80 rounded-xl p-5" data-testid={`concept-${c.id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <h3 className="font-display font-semibold text-slate-100">{c.name}</h3>
          <p className="text-xs text-slate-500 flex items-center gap-1.5"><Tag className="w-3 h-3" /> {c.niche} · {c.language}</p>
        </div>
        {c.channel_id
          ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" data-testid={`concept-launched-${c.id}`}>Profile created</span>
          : <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400 border border-slate-700">Candidate</span>}
      </div>
      <p className="text-xs text-slate-400 flex items-center gap-1.5 mb-3"><Users className="w-3 h-3" /> {c.target_audience}</p>

      <div className="space-y-1.5 mb-3">
        <ScoreBar label="Demand" v={c.scores?.demand} />
        <ScoreBar label="Durability" v={c.scores?.durability} />
        <ScoreBar label="Competition" v={c.scores?.competition} />
        <ScoreBar label="Feasibility" v={c.scores?.feasibility} />
      </div>
      <p className="text-[11px] text-amber-300/80 mb-2" data-testid={`estimate-label-${c.id}`}>AI estimates — {c.score_reasons || "reasoning provided by the strategist model"}</p>

      <div className="flex flex-wrap gap-1 mb-2">
        {(c.content_pillars || []).map((p, i) => <span key={`${c.id}-pillar-${p}-${i}`} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-300 border border-slate-700">{p}</span>)}
      </div>
      <p className="text-xs text-slate-500 mb-1"><Calendar className="w-3 h-3 inline mr-1" /> {(c.calendar || []).length}-week calendar · {(c.calendar || []).reduce((n, w) => n + (w.ideas?.length || 0), 0)} ideas</p>
      <p className="text-[11px] text-slate-600 mb-1">Duration: {c.duration_strategy} <span className="text-amber-300/70">({c.duration_strategy_note})</span></p>
      <p className="text-[11px] text-slate-600 mb-3">Research: {c.research_provenance} · brand: {c.branding?.note}</p>

      {/* Brand Kit */}
      <BrandKitSection concept={c} onUpdated={onCreated} />

      <div className="mt-3">
        {c.channel_id
          ? <Button size="sm" variant="outline" onClick={() => navigate(`/channels/${c.channel_id}`)} className="border-slate-700 text-slate-300 hover:bg-slate-800 w-full" data-testid={`open-channel-${c.id}`}>Open channel profile</Button>
          : <Button size="sm" onClick={create} disabled={busy} className="bg-violet-600 hover:bg-violet-500 text-white w-full" data-testid={`create-channel-${c.id}`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Rocket className="w-4 h-4 mr-1.5" /> Create channel profile</>}</Button>}
      </div>
    </div>
  );
}

export default function ChannelFactory() {
  const { loadChannels } = useStudio();
  const [runs, setRuns] = useState(null);
  const [active, setActive] = useState(null);
  const [error, setError] = useState(null);
  const [theme, setTheme] = useState("");
  const [count, setCount] = useState(3);
  const [launching, setLaunching] = useState(false);
  const [settings, setSettings] = useState(null);
  const [report, setReport] = useState(null);

  const load = async () => {
    setError(null);
    try {
      const [r, s, rep] = await Promise.all([api.get("/factory/portfolios"), api.get("/factory/settings"), api.get("/factory/weekly-report")]);
      setRuns(r.data); setSettings(s.data); setReport(rep.data);
      if (r.data.length && !active) openRun(r.data[0].id);
    } catch (e) { setError(apiError(e)); }
  };
  const openRun = async (id) => { try { const { data } = await api.get(`/factory/portfolios/${id}`); setActive(data); } catch (e) { toast.error(apiError(e)); } };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount; auto-opens the first run only on initial load
  }, []);

  const launch = async () => {
    if (!theme.trim()) { toast.error("Enter a theme"); return; }
    setLaunching(true);
    try {
      const { data } = await api.post("/factory/portfolios", { theme: theme.trim(), count: Number(count) || 3 });
      if (data.status === "blocked") toast.error(data.blocked_reason);
      else if (data.idempotent) toast.message(data.note);
      else toast.success(`${(data.concepts || []).length} concepts generated`);
      setActive(data); await load(); await loadChannels();
    } catch (e) { toast.error(apiError(e)); } finally { setLaunching(false); }
  };
  const pauseResume = async (id, action) => { try { await api.post(`/factory/portfolios/${id}/${action}`); toast.success(action === "pause" ? "Paused" : "Resumed"); await load(); await openRun(id); } catch (e) { toast.error(apiError(e)); } };

  if (error) return <ErrorState message={error} onRetry={load} />;

  return (
    <div className="animate-in-up">
      <div className="mb-6">
        <p className="text-[11px] font-semibold tracking-widest uppercase text-violet-400 mb-1 flex items-center gap-1.5"><Factory className="w-3.5 h-3.5" /> Autopilot</p>
        <h1 className="text-3xl sm:text-4xl font-display font-extrabold tracking-tight text-slate-50">Channel Factory</h1>
        <p className="text-sm text-slate-400 mt-2">Generate channel concepts + launch packages from a theme. Concepts are internal profiles — never live YouTube channels. All scores are AI estimates.</p>
      </div>

      {/* Command box */}
      <div className="bg-[#111827] border border-slate-800/80 rounded-xl p-5 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px_auto] gap-3 items-end">
          <div><Label className="text-slate-300 text-sm mb-1 block">Theme</Label><Input className={inputCls} placeholder="e.g. AI tools for beginners" value={theme} onChange={(e) => setTheme(e.target.value)} data-testid="factory-theme" /></div>
          <div><Label className="text-slate-300 text-sm mb-1 block">Concepts (max 10)</Label><Input type="number" min={1} max={10} className={inputCls} value={count} onChange={(e) => setCount(e.target.value)} data-testid="factory-count" /></div>
          <Button onClick={launch} disabled={launching} className="bg-violet-600 hover:bg-violet-500 text-white" data-testid="factory-launch">{launching ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1.5" /> Launch run</>}</Button>
        </div>
        {settings && <p className="text-xs text-slate-500 mt-3">Portfolio cap: <span className="text-slate-300">${settings.monthly_budget_cap}</span> · spent this month: <span className="text-slate-300">${settings.month_spend}</span> · default cadence {settings.default_videos_per_week}/wk · default mode {settings.default_publishing_mode}</p>}
      </div>

      {!runs ? <LoadingState label="Loading factory…" /> : runs.length === 0 ? (
        <EmptyState icon={Factory} title="No portfolio runs yet" description="Enter a theme above and launch your first channel concept run." />
      ) : (
        <>
          {/* Run selector */}
          <div className="flex flex-wrap gap-2 mb-5" data-testid="run-list">
            {runs.map((r) => (
              <button key={r.id} onClick={() => openRun(r.id)} data-testid={`run-${r.id}`}
                className={`text-xs px-3 py-1.5 rounded-full border ${active?.id === r.id ? "bg-violet-600 text-white border-violet-500" : "bg-slate-900 text-slate-400 border-slate-800"}`}>
                {r.theme} · {r.status} · {r.concept_count}
              </button>
            ))}
          </div>

          {active && (
            <div>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div>
                  <h2 className="text-lg font-display font-bold text-slate-100">{active.theme}</h2>
                  <p className="text-xs text-slate-500">Status {active.status} · cost ${active.cost} · {active.research_provenance}</p>
                </div>
                <div className="flex gap-2">
                  {active.status === "paused"
                    ? <Button size="sm" variant="outline" onClick={() => pauseResume(active.id, "resume")} className="border-slate-700 text-slate-300" data-testid="resume-run"><Play className="w-4 h-4 mr-1.5" /> Resume</Button>
                    : <Button size="sm" variant="outline" onClick={() => pauseResume(active.id, "pause")} className="border-slate-700 text-slate-300" data-testid="pause-run"><Pause className="w-4 h-4 mr-1.5" /> Pause</Button>}
                </div>
              </div>
              {active.status === "blocked" && <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg px-4 py-3 text-sm text-rose-300 mb-4 flex items-center gap-2" data-testid="run-blocked"><Ban className="w-4 h-4" /> {active.blocked_reason}</div>}
              {active.rejected?.length > 0 && <div className="text-xs text-slate-500 mb-3" data-testid="rejected-list">Rejected: {active.rejected.map((r) => `${r.name} (${r.reason})`).join("; ")}</div>}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="concepts-grid">
                {(active.concepts || []).map((c) => <ConceptCard key={c.id} c={c} onCreated={() => { openRun(active.id); load(); }} />)}
              </div>
            </div>
          )}

          {/* Weekly report */}
          {report && report.channels.length > 0 && (
            <div className="mt-10 bg-[#111827] border border-slate-800/80 rounded-xl p-5" data-testid="weekly-report">
              <h2 className="text-lg font-display font-bold text-slate-100 mb-1 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-violet-300" /> Weekly report</h2>
              <p className="text-xs text-slate-500 mb-4">{report.note}</p>
              <div className="space-y-2">
                {report.channels.map((r) => (
                  <div key={r.channel_id} className="flex items-center justify-between bg-slate-900/50 border border-slate-800 rounded-lg px-4 py-2.5 text-sm" data-testid={`report-${r.channel_id}`}>
                    <span className="text-slate-200">{r.name}</span>
                    <span className="text-xs text-slate-500">produced {r.produced} · backlog {r.backlog_ideas} · <DollarSign className="w-3 h-3 inline" />{r.cost} · {r.publishing_mode}{r.blocked && <span className="text-rose-400 ml-1">· blocked ({r.blocked_reason})</span>}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
